/**
 * Placeholder art — US 17.07, spec in 111. Programmatically generated flat
 * colours; every texture key is 1:1 replaceable by a real asset of the same
 * key and dimensions, so final art lands with zero code change.
 *
 * Colour-coding rule: walkable, blocked and interactive must be tellable
 * apart at a glance.
 */
import Phaser from 'phaser';
import type { TileKind } from '@logic/grid.js';
import { SPRITE_H, SPRITE_W, TILE } from '@shared/constants.js';
import type { Facing } from '@logic/grid.js';
import { ENV } from './palette.js';

/** Flat tile colours from the locked environment palette (US 17.09):
 *  cool/earthy = walkable, dark = blocked, warm = interactive. */
const TILE_COLOURS: Record<TileKind, number> = {
  grass: ENV.grass,
  road: ENV.road,
  sidewalk: ENV.sidewalk,
  sand: ENV.sand,
  dock: ENV.dock,
  interior: ENV.interior,
  wall: ENV.wall,
  water: ENV.water,
  door: ENV.door,
};

export const tileKey = (kind: TileKind) => `tile-${kind}`;
export const playerKey = (facing: Facing) => `player-${facing}`;
export const npcKey = (facing: Facing) => `npc-${facing}`;
export const INDICATOR_KEY = 'indicator-interact';
export const BAG_KEY = 'death-bag';
export const CAR_KEY = 'hazard-car';
export const WARNING_KEY = 'hazard-warning';
export const LOCK_KEY = 'area-lock';
export const SPOT_KEY = 'fishing-spot';

const FACINGS: Facing[] = ['up', 'down', 'left', 'right'];

function drawTile(scene: Phaser.Scene, kind: TileKind): void {
  const g = scene.add.graphics();
  g.fillStyle(TILE_COLOURS[kind], 1);
  g.fillRect(0, 0, TILE, TILE);
  // Subtle grid border so tile boundaries stay readable during layout work.
  g.lineStyle(1, 0x000000, 0.08);
  g.strokeRect(0, 0, TILE, TILE);
  g.generateTexture(tileKey(kind), TILE, TILE);
  g.destroy();
}

/**
 * 48×64 character: body block + head, with the facing shown two ways —
 * a white marker triangle on the facing edge, and a per-facing body shade.
 * Four-direction distinction by colour is the 17.07 requirement.
 */
function drawCharacter(scene: Phaser.Scene, base: number, key: (f: Facing) => string): void {
  const shade: Record<Facing, number> = {
    down: base,
    up: Phaser.Display.Color.IntegerToColor(base).darken(15).color,
    left: Phaser.Display.Color.IntegerToColor(base).lighten(10).color,
    right: Phaser.Display.Color.IntegerToColor(base).lighten(20).color,
  };
  for (const facing of FACINGS) {
    const g = scene.add.graphics();
    // Body: lower 40px block, centred 32px wide (mirrors the collision box).
    g.fillStyle(shade[facing], 1);
    g.fillRect(8, 24, 32, 40);
    // Head.
    g.fillStyle(0xffe0bd, 1);
    g.fillCircle(24, 16, 12);
    // Facing marker: white triangle on the corresponding edge of the body.
    g.fillStyle(0xffffff, 1);
    if (facing === 'down') g.fillTriangle(18, 56, 30, 56, 24, 63);
    if (facing === 'up') g.fillTriangle(18, 30, 30, 30, 24, 24);
    if (facing === 'left') g.fillTriangle(14, 38, 14, 50, 8, 44);
    if (facing === 'right') g.fillTriangle(34, 38, 34, 50, 40, 44);
    g.generateTexture(key(facing), SPRITE_W, SPRITE_H);
    g.destroy();
  }
}

function drawIndicator(scene: Phaser.Scene): void {
  const g = scene.add.graphics();
  // Warm yellow "!" on a dark pill — interactive colour family.
  g.fillStyle(ENV.shadow, 0.85);
  g.fillRoundedRect(6, 0, 20, 28, 6);
  g.fillStyle(ENV.accent, 1);
  g.fillRect(14, 4, 4, 14);
  g.fillRect(14, 21, 4, 4);
  g.generateTexture(INDICATOR_KEY, 32, 28);
  g.destroy();
}

function drawBag(scene: Phaser.Scene): void {
  const g = scene.add.graphics();
  // Dropped-loot sack (US 01.07): warm brown, interactive colour family.
  g.fillStyle(ENV.bagBody, 1);
  g.fillRoundedRect(4, 10, 24, 20, 6);
  g.fillStyle(ENV.bagNeck, 1);
  g.fillRect(12, 4, 8, 8); // tied neck
  g.lineStyle(2, ENV.accent, 0.9);
  g.strokeRoundedRect(4, 10, 24, 20, 6);
  g.generateTexture(BAG_KEY, 32, 32);
  g.destroy();
}

function drawCar(scene: Phaser.Scene): void {
  const g = scene.add.graphics();
  // The hazard car (US 03.07): danger red, one tile long, windows so it
  // reads as a vehicle and not a crate.
  g.fillStyle(ENV.carBody, 1);
  g.fillRoundedRect(2, 8, 44, 26, 6);
  g.fillStyle(ENV.carWindow, 1);
  g.fillRect(8, 12, 12, 10);
  g.fillRect(26, 12, 12, 10);
  g.fillStyle(ENV.carWheel, 1);
  g.fillCircle(12, 36, 5);
  g.fillCircle(36, 36, 5);
  g.generateTexture(CAR_KEY, TILE, 42);
  g.destroy();
}

function drawWarning(scene: Phaser.Scene): void {
  const g = scene.add.graphics();
  // The ≥1s spawn tell (US 03.07): amber triangle at the hazard's origin.
  g.fillStyle(ENV.warning, 1);
  g.fillTriangle(16, 2, 2, 28, 30, 28);
  g.fillStyle(ENV.shadow, 1);
  g.fillRect(14, 10, 4, 10);
  g.fillRect(14, 23, 4, 3);
  g.generateTexture(WARNING_KEY, 32, 30);
  g.destroy();
}

function drawLock(scene: Phaser.Scene): void {
  const g = scene.add.graphics();
  // Locked-area blocker (US 03.01): dark padlock over the blocker tile.
  g.fillStyle(ENV.shadow, 0.55);
  g.fillRect(0, 0, TILE, TILE);
  g.fillStyle(ENV.accent, 1);
  g.fillRoundedRect(14, 22, 20, 16, 3);
  g.lineStyle(4, ENV.accent, 1);
  g.strokeCircle(24, 20, 7);
  g.generateTexture(LOCK_KEY, TILE, TILE);
  g.destroy();
}

function drawSpot(scene: Phaser.Scene): void {
  const g = scene.add.graphics();
  // Fishing-spot affordance (US 20.02): bobber sign, interactive family.
  g.fillStyle(ENV.accent, 1);
  g.fillRect(14, 6, 4, 22);
  g.fillStyle(ENV.carBody, 1);
  g.fillCircle(16, 8, 6);
  g.fillStyle(0xffffff, 1);
  g.fillCircle(16, 6, 3);
  g.generateTexture(SPOT_KEY, 32, 30);
  g.destroy();
}

/** Generates every placeholder texture into the scene's texture manager. */
export function generatePlaceholderTextures(scene: Phaser.Scene): void {
  for (const kind of Object.keys(TILE_COLOURS) as TileKind[]) drawTile(scene, kind);
  drawCharacter(scene, 0x1e88e5, playerKey); // player: blue family
  drawCharacter(scene, 0x8e24aa, npcKey); // NPCs: purple family
  drawIndicator(scene);
  drawBag(scene);
  drawCar(scene);
  drawWarning(scene);
  drawLock(scene);
  drawSpot(scene);
}
